/**
 * React Query hooks for SecretManager module.
 * Replaces: useSecretsExplorer → SecretsExplorer → BackendAPI
 *           useChangeRequests → ChangeRequests → BackendAPI
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '@app/auth';
import { fetchApiWithErrors, ApiError } from '@app/hooks/api';
import {
  ChangeRequest,
  ChangeRequestAction,
  ChangeRequestCreateResult,
  ChangeRequestDetail,
  SecretRef,
  SecretWithValue,
} from '../../../../shared/secretmanager/changerequests/types';
import { SecretDetail, ApiUnsealResponse } from '../../../../shared/secretmanager/secretsexplorer/types';
import {
  parse_namespaces_json,
  parse_keyvaults_json,
  Namespace,
  Keyvaults,
} from '../SecretsExplorer/model/repository';

// ═══════════════════════════════════════════════════════════════════
// SecretsExplorer Hooks
// ═══════════════════════════════════════════════════════════════════

/** Fetches namespaces and keyvaults from /api/secretmanager.json */
export function useSecretManagerNamespaces() {
  const getToken = useAccessToken();

  return useQuery<{ namespaces: Namespace[]; keyvaults: Keyvaults }>({
    queryKey: ['secretManager', 'namespaces'],
    queryFn: async () => {
      const data = await fetchApiWithErrors<{ namespaces: any; keyvaults: any }>(
        '/api/secretmanager.json',
        getToken,
      );
      return {
        namespaces: parse_namespaces_json(data.namespaces),
        keyvaults: parse_keyvaults_json(data.keyvaults),
      };
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/** Fetches secret detail (history + actions) for a given vault/name */
export function useSecretDetail(vault: string, name: string, enabled: boolean = true) {
  const getToken = useAccessToken();

  return useQuery<SecretDetail>({
    queryKey: ['secretManager', 'secretDetail', vault, name],
    queryFn: () =>
      fetchApiWithErrors<SecretDetail>(
        `/api/secretmanager/secretsexplorer/get?vault=${encodeURIComponent(vault)}&name=${encodeURIComponent(name)}`,
        getToken,
      ),
    enabled: enabled && !!vault && !!name,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/** Mutation to unseal (reveal) a secret value in SecretsExplorer */
export function useUnsealSecret() {
  const getToken = useAccessToken();

  return useMutation<ApiUnsealResponse, ApiError, SecretRef>({
    mutationFn: (data: SecretRef) =>
      fetchApiWithErrors<ApiUnsealResponse>(
        `/api/secretmanager/secretsexplorer/unseal?vault=${encodeURIComponent(data.vault)}&name=${encodeURIComponent(data.name)}`,
        getToken,
      ),
  });
}

// ═══════════════════════════════════════════════════════════════════
// ChangeRequests Hooks
// ═══════════════════════════════════════════════════════════════════

/** Fetches the list of all change requests */
export function useChangeRequestsList() {
  const getToken = useAccessToken();

  return useQuery<ChangeRequest[]>({
    queryKey: ['secretManager', 'changeRequests'],
    queryFn: async () => {
      const data = await fetchApiWithErrors<ChangeRequest[]>(
        '/api/secretmanager/changerequests',
        getToken,
      );
      return data.reverse();
    },
    staleTime: 60 * 1000,
    retry: 2,
  });
}

/** Fetches a single change request detail by ID */
export function useChangeRequestDetail(id: string) {
  const getToken = useAccessToken();

  return useQuery<ChangeRequestDetail>({
    queryKey: ['secretManager', 'changeRequest', id],
    queryFn: () =>
      fetchApiWithErrors<ChangeRequestDetail>(
        `/api/secretmanager/changerequests/${id}`,
        getToken,
      ),
    enabled: !!id,
    staleTime: 30 * 1000,
    retry: 2,
  });
}

/** Mutation to unseal a secret within a change request */
export function useUnsealChangeRequestSecret() {
  const getToken = useAccessToken();

  return useMutation<ApiUnsealResponse, ApiError, { id: number; secret: SecretRef }>({
    mutationFn: (data) =>
      fetchApiWithErrors<ApiUnsealResponse>(
        `/api/secretmanager/changerequests/${data.id}/secrets?vault=${encodeURIComponent(data.secret.vault)}&name=${encodeURIComponent(data.secret.name)}`,
        getToken,
      ),
  });
}

/** Mutation to trigger an action (approve, apply, cancel, etc.) on a change request */
export function useTriggerChangeRequestAction() {
  const getToken = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: number; action: ChangeRequestAction }>({
    mutationFn: async (data) => {
      await fetchApiWithErrors<void>(
        `/api/secretmanager/changerequests/${data.id}/${data.action}`,
        getToken,
        { method: 'POST' },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secretManager', 'changeRequests'] });
      queryClient.invalidateQueries({ queryKey: ['secretManager', 'changeRequest'] });
    },
  });
}

/** Mutation to create a new change request */
export function useCreateChangeRequest() {
  const getToken = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation<
    ChangeRequestCreateResult,
    ApiError,
    { ticket: string; description: string; secrets: SecretWithValue[] }
  >({
    mutationFn: (data) =>
      fetchApiWithErrors<ChangeRequestCreateResult>(
        '/api/secretmanager/changerequests/create',
        getToken,
        {
          method: 'POST',
          body: JSON.stringify({
            ticket: data.ticket,
            description: data.description,
            secrets: data.secrets,
          }),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secretManager', 'changeRequests'] });
    },
  });
}
